import {computed, ref} from "vue";
import {useUserState} from "./useUserState.js";

// Presentation for the achievements the backend awards. The ids here MUST match
// AchievementCatalog.cs on the server, which is the single source of truth for which
// achievements exist and when each unlocks: only the name/icon/description live here.
// Same mirrored-contract pattern as the cipher key and SERIES_LENGTH.
// Ordered by time horizon, shortest first (skill is per-puzzle, days is lifetime), so the
// most immediately attemptable badges lead the "All" view. The tab bar mirrors this order.
export const ACHIEVEMENTS = [
    // Skill
    {id: "flawless", category: "skill", name: "Flawless", icon: "✨", description: "Clear a puzzle without a single wrong guess"},
    {id: "clutch", category: "skill", name: "Clutch", icon: "😅", description: "Win a puzzle holding just one heart"},
    {id: "brimming", category: "skill", name: "Brimming", icon: "🧡", description: "Finish a puzzle with a full heart pool"},
    {id: "sharpshooter", category: "skill", name: "Sharpshooter", icon: "🎯", description: "Clear a puzzle flawlessly 20+ deep in a run"},
    {id: "cold-read", category: "skill", name: "Cold Read", icon: "🔮", description: "Commit a word with 4+ letters still hidden"},
    {id: "blind", category: "skill", name: "Blindfolded", icon: "🙈", description: "Commit a word with 6+ letters still hidden"},
    {id: "scrabbler", category: "skill", name: "Scrabbler", icon: "🃏", description: "Clear a puzzle with a Q, Z or X in it"},
    {id: "purist", category: "skill", name: "Purist", icon: "⌨️", description: "Clear a puzzle using only word guesses"},
    {id: "telepath", category: "skill", name: "Telepath", icon: "🎩", description: "Clear a puzzle flawlessly using only word guesses"},
    {id: "comeback", category: "skill", name: "Comeback", icon: "💗", description: "Clear a puzzle after dropping to one heart"},
    {id: "quick", category: "skill", name: "Quick", icon: "⏱️", description: "Solve a puzzle in under 10 seconds"},
    {id: "lightning", category: "skill", name: "Lightning", icon: "🏎️", description: "Solve a puzzle in under 5 seconds"},
    {id: "flash", category: "skill", name: "Blink", icon: "💨", description: "Clear a puzzle flawlessly in under 5 seconds"},
    {id: "marksman", category: "skill", name: "Marksman", icon: "🏹", description: "Clear 5 puzzles flawlessly"},
    {id: "deadeye", category: "skill", name: "Deadeye", icon: "👁️", description: "Clear 25 puzzles flawlessly"},
    {id: "flawless-50", category: "skill", name: "Virtuoso", icon: "🎻", description: "Clear 50 puzzles flawlessly"},
    {id: "perfectionist", category: "skill", name: "Perfectionist", icon: "💠", description: "Clear 100 puzzles flawlessly"},
    {id: "perfect-2", category: "skill", name: "Back to Back", icon: "✌️", description: "Clear 2 puzzles in a row without a wrong guess"},
    {id: "perfect-5", category: "skill", name: "On a Roll", icon: "🎳", description: "Clear 5 puzzles in a row without a wrong guess"},
    {id: "perfect-10", category: "skill", name: "Perfect Run", icon: "🌈", description: "Clear 10 puzzles in a row without a wrong guess"},
    {id: "lifeline", category: "skill", name: "Lifeline", icon: "🆘", description: "Use a hint for the first time"},
    {id: "big-spender", category: "skill", name: "Big Spender", icon: "💸", description: "Use 3 hints on a single puzzle"},
    {id: "clutch-hint", category: "skill", name: "Hail Mary", icon: "🙏", description: "Take a hint with 2 hearts left and still clear the puzzle"},
    {id: "self-made", category: "skill", name: "Self-Made", icon: "🧗", description: "Clear 10 puzzles in a row without a hint"},
    {id: "mastermind", category: "skill", name: "Mastermind", icon: "🧩", description: "Clear 5 puzzles in a row using only word guesses"},
    // Runs
    {id: "run-5", category: "runs", name: "Getting Going", icon: "🥾", description: "Clear 5 puzzles in one run"},
    {id: "survivor", category: "runs", name: "Survivor", icon: "🛡️", description: "Clear 10 puzzles in one run"},
    {id: "run-20", category: "runs", name: "Determined", icon: "💪", description: "Clear 20 puzzles in one run"},
    {id: "marathon", category: "runs", name: "Marathon", icon: "🏃", description: "Clear 35 puzzles in one run"},
    {id: "unstoppable", category: "runs", name: "Unstoppable", icon: "🚀", description: "Clear 50 puzzles in one run"},
    {id: "run-75", category: "runs", name: "Relentless", icon: "⚔️", description: "Clear 75 puzzles in one run"},
    {id: "ironwill", category: "runs", name: "Iron Will", icon: "🏔️", description: "Clear 100 puzzles in one run"},
    // Streaks
    {id: "streak-3", category: "streaks", name: "Warming Up", icon: "⚡", description: "Reach a 3-day streak"},
    {id: "regular", category: "streaks", name: "On Fire", icon: "🔥", description: "Reach a 7-day streak"},
    {id: "streak-14", category: "streaks", name: "Two Weeks", icon: "🌟", description: "Reach a 14-day streak"},
    {id: "streak-30", category: "streaks", name: "Monthly Master", icon: "🏆", description: "Reach a 30-day streak"},
    {id: "streak-60", category: "streaks", name: "Two Months", icon: "🌙", description: "Reach a 60-day streak"},
    {id: "streak-100", category: "streaks", name: "Centurion", icon: "💯", description: "Reach a 100-day streak"},
    {id: "streak-365", category: "streaks", name: "Year One", icon: "🎆", description: "Reach a 365-day streak"},
    // Score
    {id: "first-clear", category: "score", name: "First Steps", icon: "🌱", description: "Clear your first puzzle"},
    {id: "score-50", category: "score", name: "Novice", icon: "🌿", description: "Reach 50 total score"},
    {id: "score-100", category: "score", name: "Apprentice", icon: "📝", description: "Reach 100 total score"},
    {id: "score-500", category: "score", name: "Wordsmith", icon: "📚", description: "Reach 500 total score"},
    {id: "score-1500", category: "score", name: "Scholar", icon: "🎓", description: "Reach 1,500 total score"},
    {id: "score-3000", category: "score", name: "Lexicon", icon: "📖", description: "Reach 3,000 total score"},
    {id: "score-6000", category: "score", name: "Savant", icon: "🧠", description: "Reach 6,000 total score"},
    {id: "score-10000", category: "score", name: "Grandmaster", icon: "🧙", description: "Reach 10,000 total score"},
    // XP
    {id: "xp-1000", category: "xp", name: "Charged", icon: "🔋", description: "Reach 1,000 XP"},
    {id: "xp-10000", category: "xp", name: "Live Wire", icon: "🔌", description: "Reach 10,000 XP"},
    {id: "xp-50000", category: "xp", name: "Dynamo", icon: "🌀", description: "Reach 50,000 XP"},
    {id: "xp-150000", category: "xp", name: "Powerhouse", icon: "🏭", description: "Reach 150,000 XP"},
    {id: "xp-350000", category: "xp", name: "Overdrive", icon: "💥", description: "Reach 350,000 XP"},
    {id: "xp-600000", category: "xp", name: "Titan", icon: "🗿", description: "Reach 600,000 XP"},
    {id: "xp-1000000", category: "xp", name: "Millionaire", icon: "💰", description: "Reach 1,000,000 XP"},
    // Days
    {id: "day-1", category: "days", name: "Day One", icon: "🌅", description: "Complete your first day"},
    {id: "days-10", category: "days", name: "Hooked", icon: "🔁", description: "Play on 10 days"},
    {id: "veteran", category: "days", name: "Veteran", icon: "🎖️", description: "Play on 50 days"},
    {id: "seasoned", category: "days", name: "Seasoned", icon: "🏅", description: "Play on 200 days"},
    {id: "days-500", category: "days", name: "Devotee", icon: "💎", description: "Play on 500 days"},
    {id: "days-1000", category: "days", name: "Lifer", icon: "👑", description: "Play on 1,000 days"},
    // Account
    {id: "logged-in", category: "account", name: "Signed In", icon: "🔑", description: "Log in to your Syndle account"},
    {id: "name-changed", category: "account", name: "Make Your Mark", icon: "🏷️", description: "Choose your own display name"},
    {id: "share", category: "account", name: "Spread the Word", icon: "📣", description: "Share your result"},
];

// Module-level singleton so the game engine can enqueue toasts and the single global toast
// component (mounted in App.vue) can render them, wherever each sits in the tree.
const toastQueue = ref([]);

// Queue a toast for each given id without touching user state. Standalone (not on the
// useAchievements() composable) so callers that already hold the fresh user object (e.g. the
// login / name-change flows in useUserState) can toast their newAchievements without the
// composable's useUserState dependency, which would otherwise recurse.
export function enqueueAchievementToasts(ids) {
    if (!ids) {
        return;
    }
    for (const id of ids) {
        const def = ACHIEVEMENTS.find(a => a.id === id);
        if (def) {
            toastQueue.value.push(def);
        }
    }
}

// Global rarity table from the server ({totalPlayers, counts}), shared by everyone and only
// refreshed every 12h server-side, so we fetch it at most once per session.
const globalStats = ref(null);
let statsPromise = null;

function loadAchievementStats() {
    if (!statsPromise) {
        statsPromise = fetch(`${import.meta.env.VITE_APP_API_BASE_URL}/api/achievements/stats`)
            .then(res => res.ok ? res.json() : null)
            .then(json => {
                if (json) {
                    globalStats.value = json;
                }
            })
            .catch(e => console.error("Failed to load achievement stats", e));
    }
    return statsPromise;
}

// Top-100 most-badges leaderboard, also computed server-side on the 12h tick. Loaded lazily
// (only when the leaderboard tab is opened) and held module-level so reopening the tab is free.
const leaderboard = ref([]);
const leaderboardLoading = ref(false);
let leaderboardPromise = null;

function loadAchievementLeaderboard() {
    if (!leaderboardPromise) {
        leaderboardLoading.value = true;
        leaderboardPromise = fetch(`${import.meta.env.VITE_APP_API_BASE_URL}/api/achievements/leaderboard`)
            .then(res => res.ok ? res.json() : null)
            .then(json => {
                if (json) {
                    leaderboard.value = json;
                }
            })
            .catch(e => console.error("Failed to load achievement leaderboard", e))
            .finally(() => { leaderboardLoading.value = false; });
    }
    return leaderboardPromise;
}

export const useAchievements = () => {
    const {syndleUser} = useUserState();

    // Unlocked ids are owned by the server and ride along on the user response.
    const unlockedIds = computed(() => syndleUser.value.achievements ?? []);

    function isUnlocked(id) {
        return unlockedIds.value.includes(id);
    }

    // Called with the ids the server just reported as newly unlocked: mirror them into the
    // local user state (so the list updates without a reload) and queue a toast for each.
    function unlock(ids) {
        if (!ids || ids.length === 0) {
            return;
        }
        if (!syndleUser.value.achievements) {
            syndleUser.value.achievements = [];
        }
        for (const id of ids) {
            if (!syndleUser.value.achievements.includes(id)) {
                syndleUser.value.achievements.push(id);
            }
        }
        enqueueAchievementToasts(ids);
    }

    function dismissToast(id) {
        toastQueue.value = toastQueue.value.filter(a => a.id !== id);
    }

    // Global rarity for an achievement: the share of qualifying players who hold it, bucketed
    // into a label. Returns null until the stats have loaded (and while nobody qualifies yet).
    function rarity(id) {
        const stats = globalStats.value;
        if (!stats || !stats.totalPlayers) {
            return null;
        }
        const count = stats.counts?.[id] ?? 0;
        const pct = (count / stats.totalPlayers) * 100;
        let label, tier;
        if (pct >= 50) {
            [label, tier] = ["Common", "common"];
        } else if (pct >= 20) {
            [label, tier] = ["Uncommon", "uncommon"];
        } else if (pct >= 5) {
            [label, tier] = ["Rare", "rare"];
        } else {
            [label, tier] = ["Legendary", "legendary"];
        }
        // Show "<1" rather than rounding a fraction of a percent down to a misleading 0.
        const percent = pct > 0 && pct < 1 ? "<1" : String(Math.round(pct));
        return {label, tier, percent};
    }

    return {
        achievements: ACHIEVEMENTS,
        unlockedIds,
        isUnlocked,
        toastQueue,
        unlock,
        dismissToast,
        loadAchievementStats,
        loadAchievementLeaderboard,
        leaderboard,
        leaderboardLoading,
        rarity,
    };
};
